// combatpro/panels/monsters/studio/useMonsterStudioTemplates.js
//
// Combat PRO - Encounter Template runtime (Stage 63: Legacy Template System
// Migration).
//
// The runtime for the legacy Encounter Templates subsystem, expressed as a React
// hook. It OWNS template behaviour only - the template list, the modal open state,
// boot loading, Save Current Setup, Load, Duplicate, and Delete - and recreates none
// of the established layers' logic. It consumes:
//   - the Stage 63 engine   (buildTemplateEntries / createTemplateRecord /
//                            cloneTemplate) for the pure data transforms;
//   - the Stage 63 persistence functions (loadUserTemplates / saveUserTemplate /
//                            deleteUserTemplate) on the existing /user/monsters store;
//   - the Stage 61 selection runtime (useMonsterSelection.selectMonster) as the single
//                            authoritative encounter insertion path - Load drives it;
//   - CombatContext         (state.monsters read for the current line-up, switchView
//                            for the post-load view change) - read only, never modified.
//
// It does NOT own persistence (the Stage 58/63 module does), selection (Stage 61
// does), the editor, the library, or CombatContext. No runtime ownership moves.
//
// Faithful to the legacy (battletracker.html):
//   open / close   <- viTplOpen / viTplClose (11532-11533): modal show / hide. The
//                     legacy viTplOpen also called viTplRender to repaint the list;
//                     here the list is live React state, so opening simply reveals it.
//   saveCurrent    <- viTplSaveCurrent (11544): read the active line-up; if empty,
//                     alert and stop; window.prompt for a name (default 'New Encounter
//                     Template'); a falsy / cancelled prompt stops; otherwise mint a
//                     template record and persist it. Toast 'Template saved'.
//   load           <- viTplLoad (11562): guard the selection runtime; from slot 0,
//                     place each entry count times via the Stage 61 selectMonster, cap
//                     16 slots; close the modal; switch to the Monsters view; toast the
//                     slot count. The legacy DOM refreshes (vi_populateMonsterSelectors
//                     / updateEncounterSummary) are dropped - panels render from state
//                     and selectMonster bumps the selection version declaratively.
//   duplicate      <- viTplDuplicate (11566): clone the record (new id, " (Copy)",
//                     fresh ts) and persist it. Silent on success (legacy was silent).
//   remove         <- viTplDelete (11571): window.confirm 'Delete this template?';
//                     on confirm, delete from the store and drop it from the list.
//
// Persistence substitution only (the brief's single permitted change): the legacy
// localStorage / session-cloud array (viTplPersist / viTplBoot) is replaced by
// per-record cloud CRUD on the existing /user/monsters endpoint, following the Stage
// 58 POST-first-then-commit discipline (the list is updated only after a successful
// call), with cloud-failure surfaced via the studio toast. Every workflow, prompt,
// and interaction is otherwise the legacy behaviour.
//
// No DOM beyond window.prompt / window.confirm / window.alert (the legacy prompts,
// kept faithful). No querySelector, no innerHTML, no localStorage, no fetch here. No
// optional chaining. No em dash.

import { useState, useEffect, useRef, useContext } from 'react';
import { CombatContext } from '../../../contexts/CombatContext';
import useMonsterSelection from '../../../monster/useMonsterSelection';
import {
  buildTemplateEntries,
  createTemplateRecord,
  cloneTemplate
} from '../../../monster/monsterTemplateEngine';
import {
  loadUserTemplates,
  saveUserTemplate,
  deleteUserTemplate
} from './monsterPersistence';

function useMonsterStudioTemplates() {
  const selection = useMonsterSelection();
  const combat = useContext(CombatContext);

  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const messageIdRef = useRef(0);
  const [messageId, setMessageId] = useState(0);

  function toast(text) {
    messageIdRef.current = messageIdRef.current + 1;
    setMessage(text);
    setMessageId(messageIdRef.current);
  }

  // Boot: load the template records once (viTplBoot, substituting cloud for the
  // legacy localStorage / session payload). The list is owned here as React state.
  useEffect(function() {
    var cancelled = false;
    (async function() {
      var loaded = await loadUserTemplates();
      if (!cancelled && Array.isArray(loaded)) {
        setTemplates(loaded);
      }
    })();
    return function() { cancelled = true; };
  }, []);

  // viTplOpen / viTplClose.
  function openTemplates() { setOpen(true); }
  function closeTemplates() { setOpen(false); }

  // viTplSaveCurrent. Read the active encounter line-up from CombatContext; if no
  // monsters are set, alert and stop; prompt for a name; persist a new template record.
  async function saveCurrent() {
    var monsters = (combat && combat.state && combat.state.monsters) ? combat.state.monsters : [];
    var entries = buildTemplateEntries(monsters);
    if (!entries.length) {
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('No monsters are set in the active encounter to save as a template.');
      }
      return false;
    }
    var name = (typeof window !== 'undefined' && window.prompt)
      ? window.prompt('Template name:', 'New Encounter Template')
      : 'New Encounter Template';
    if (!name) { return false; }
    var record = createTemplateRecord(name, entries);
    setBusy(true);
    var ok = await saveUserTemplate(record);
    setBusy(false);
    if (!ok) {
      toast('Save failed - check your connection and try again.');
      return false;
    }
    setTemplates(function(prev) { return prev.concat([record]); });
    toast('Template saved');
    return true;
  }

  // viTplLoad. Place each entry count times from slot 0 via the Stage 61
  // selectMonster, cap 16, then close + switch view + toast.
  function load(i) {
    var t = templates[i];
    if (!t) { return; }
    if (!selection || typeof selection.selectMonster !== 'function') {
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('Encounter system not ready.');
      }
      return;
    }
    var slot = 0;
    var entries = Array.isArray(t.entries) ? t.entries : [];
    for (var e = 0; e < entries.length; e++) {
      var entry = entries[e];
      var count = (entry && entry.count) ? entry.count : 0;
      for (var n = 0; n < count && slot < 16; n++) {
        selection.selectMonster(slot, entry.monsterName);
        slot++;
      }
    }
    setOpen(false);
    if (combat && typeof combat.switchView === 'function') {
      combat.switchView('view-monsters');
    }
    toast('Template loaded into ' + slot + ' slots');
  }

  // viTplDuplicate. Clone the record and persist it. Silent on success (as legacy);
  // cloud failure is surfaced via toast.
  async function duplicate(i) {
    var t = templates[i];
    if (!t) { return; }
    var copy = cloneTemplate(t);
    setBusy(true);
    var ok = await saveUserTemplate(copy);
    setBusy(false);
    if (!ok) {
      toast('Save failed - check your connection and try again.');
      return;
    }
    setTemplates(function(prev) { return prev.concat([copy]); });
  }

  // viTplDelete. Confirm, delete from the store, drop from the list. Silent on
  // success (as legacy); cloud failure is surfaced via toast.
  async function remove(i) {
    var t = templates[i];
    if (!t) { return; }
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Delete this template?')) {
      return;
    }
    setBusy(true);
    var ok = await deleteUserTemplate(t.id);
    setBusy(false);
    if (!ok) {
      toast('Delete failed - check your connection and try again.');
      return;
    }
    setTemplates(function(prev) {
      return prev.filter(function(x) { return x.id !== t.id; });
    });
  }

  return {
    open: open,
    busy: busy,
    templates: templates,
    message: message,
    messageId: messageId,
    openTemplates: openTemplates,
    closeTemplates: closeTemplates,
    saveCurrent: saveCurrent,
    load: load,
    duplicate: duplicate,
    remove: remove
  };
}

export default useMonsterStudioTemplates;
